import type { PAGE_POSITIONS } from 'types';
import type { ArticleCard as ArticleCardType } from 'types/capi/shared';

import ArticleCard from '~/components/ArticleCard';

type SharedCardProps = {
  modulePosition: number;
  moduleType: string;
  name: 'LATEST_NEWS';
  pagePosition: PAGE_POSITIONS;
};

type Props = {
  items: ArticleCardType[];
  modulePosition: number;
  moduleType: string;
  pagePosition: PAGE_POSITIONS;
};

type TileProps = {
  hideCta?: boolean;
  item: ArticleCardType;
  position: number;
  shared: SharedCardProps;
};

const TILE_COUNT = 6;

type SixCards = [
  ArticleCardType,
  ArticleCardType,
  ArticleCardType,
  ArticleCardType,
  ArticleCardType,
  ArticleCardType
];

function isSixCards(tiles: ArticleCardType[]): tiles is SixCards {
  return tiles.length === TILE_COUNT && tiles.every(Boolean);
}

function sixTiles(items: ArticleCardType[]) {
  const tiles = items.slice(0, TILE_COUNT);

  return isSixCards(tiles) ? tiles : null;
}

export function NewsTiles({
  items,
  modulePosition,
  moduleType,
  pagePosition
}: Props) {
  const cards = sixTiles(items);
  if (!cards) return null;

  const shared: SharedCardProps = {
    modulePosition,
    moduleType,
    name: 'LATEST_NEWS',
    pagePosition
  };

  return (
    <div className="w-full bg-white">
      <div className="content-width flex flex-col gap-6 py-10 md:flex-row md:py-20">
        <div className="flex flex-col gap-6 md:flex-1">
          <DesktopPortraitTile item={cards[0]} position={1} shared={shared} />
          <MobilePortraitTile item={cards[0]} position={1} shared={shared} />
          <MobileLandscapeTile item={cards[1]} position={2} shared={shared} />
          <DesktopLandscapeTile item={cards[1]} position={2} shared={shared} />
        </div>
        <div className="flex flex-col gap-6 md:flex-1">
          <MobilePortraitTile item={cards[2]} position={3} shared={shared} />
          <DesktopLandscapeTile item={cards[2]} position={3} shared={shared} />
          <SquareLandscapeTile item={cards[3]} position={4} shared={shared} />
        </div>
        <div className="flex flex-col gap-6 md:flex-1">
          <DesktopPortraitTile item={cards[4]} position={5} shared={shared} />
          <MobilePortraitTile item={cards[4]} position={5} shared={shared} />
          <MobileLandscapeTile item={cards[5]} position={6} shared={shared} />
          <DesktopLandscapeTile item={cards[5]} position={6} shared={shared} />
        </div>
      </div>
    </div>
  );
}

export function HomepageTiles({
  items,
  modulePosition,
  moduleType,
  pagePosition
}: Props) {
  const cards = sixTiles(items);
  if (!cards) return null;

  const shared: SharedCardProps = {
    modulePosition,
    moduleType,
    name: 'LATEST_NEWS',
    pagePosition
  };

  return (
    <div className="w-full bg-grey-tile">
      <div className="content-width no-scrollbar mx-auto flex gap-6 overflow-x-scroll py-10 md:py-20">
        <div className="flex flex-1 flex-col gap-6">
          <ScrollPortraitTile item={cards[0]} position={1} shared={shared} />
          <ScrollLandscapeTile item={cards[1]} position={2} shared={shared} />
        </div>
        <div className="flex flex-1 flex-col gap-6">
          <ScrollLandscapeTile item={cards[2]} position={3} shared={shared} />
          <ScrollPortraitTile item={cards[3]} position={4} shared={shared} />
        </div>
        <div className="flex flex-1 flex-col gap-6">
          <ScrollPortraitTile item={cards[4]} position={5} shared={shared} />
          <ScrollLandscapeTile item={cards[5]} position={6} shared={shared} />
        </div>
      </div>
    </div>
  );
}

function DesktopPortraitTile({ hideCta, item, position, shared }: TileProps) {
  return (
    <ArticleCard
      {...shared}
      className="hidden md:block"
      hideCta={hideCta}
      item={item}
      options={{ compact: true, titleLines: 3 }}
      position={position}
      variant="4/5-text-over"
    />
  );
}

function MobilePortraitTile({ item, position, shared }: TileProps) {
  return (
    <ArticleCard
      {...shared}
      className="max-md:w-[70vw] max-md:min-w-[285px] max-md:self-center md:hidden"
      item={item}
      options={{ compact: true, titleLines: 3 }}
      position={position}
      variant="4/5-text-over"
    />
  );
}

function MobileLandscapeTile({ item, position, shared }: TileProps) {
  return (
    <ArticleCard
      {...shared}
      className="md:hidden"
      item={item}
      options={{ compact: true, titleLines: 2 }}
      position={position}
      variant="16/9-text-over"
    />
  );
}

function DesktopLandscapeTile({ hideCta, item, position, shared }: TileProps) {
  return (
    <ArticleCard
      {...shared}
      className="hidden md:block"
      hideCta={hideCta}
      item={item}
      options={{ compact: true, titleLines: 2 }}
      position={position}
      variant="16/9-text-under"
    />
  );
}

function SquareLandscapeTile({ item, position, shared }: TileProps) {
  return (
    <ArticleCard
      {...shared}
      item={item}
      mdRatio="4:5"
      options={{ compact: true, titleLines: 2 }}
      position={position}
      variant="16/9-text-over"
    />
  );
}

function ScrollPortraitTile({ item, position, shared }: TileProps) {
  return (
    <ArticleCard
      {...shared}
      item={item}
      position={position}
      variant="4/5-text-over"
    />
  );
}

function ScrollLandscapeTile({ item, position, shared }: TileProps) {
  return (
    <ArticleCard
      {...shared}
      item={item}
      options={{ titleLines: 2 }}
      position={position}
      variant="16/9-text-under"
    />
  );
}
